import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./PromotionAnalysis.module.scss";
import { Card, Row, Col, Table, Select, Input, Typography, Tag, Space, DatePicker } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined, SearchOutlined, AlertOutlined, BulbOutlined, TrophyOutlined, PieChartOutlined, BarChartOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const fmt = (v: number) => `$${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => v.toLocaleString("es-AR");

const Vr: React.FC<{ value: number; suffix?: string }> = ({ value, suffix = "%" }) => {
  const pos = value >= 0;
  return (<span className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}>{pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {pos ? "+" : ""}{value.toFixed(1)}{suffix}</span>);
};

const VENTA_TOTAL = 54400000;

export interface Promo {
  id: number;
  nombre: string;
  tipo: string;
  ambito: string;
  ventaAtribuida: number;
  ventaBaseline: number;
  costo: number;
  costoPromo: number;
}

export const allPromos: Promo[] = [
  { id: 1, nombre: "3x2 Zapatillas Running", tipo: "MxN", ambito: "Retail + eCommerce", ventaAtribuida: 4800000, ventaBaseline: 3200000, costo: 2640000, costoPromo: 480000 },
  { id: 2, nombre: "Puntos x5 Black Friday", tipo: "Incentivo", ambito: "Todos los canales", ventaAtribuida: 3200000, ventaBaseline: 2400000, costo: 1760000, costoPromo: 320000 },
  { id: 3, nombre: "20% Off Indumentaria", tipo: "% descuento", ambito: "Retail", ventaAtribuida: 2800000, ventaBaseline: 2200000, costo: 1456000, costoPromo: 560000 },
  { id: 4, nombre: "Envío gratis > $50k", tipo: "Beneficio", ambito: "eCommerce", ventaAtribuida: 2200000, ventaBaseline: 1600000, costo: 1210000, costoPromo: 180000 },
  { id: 5, nombre: "Flash Sale Outlet", tipo: "% descuento", ambito: "Outlet", ventaAtribuida: 1400000, ventaBaseline: 1100000, costo: 910000, costoPromo: 350000 },
  { id: 6, nombre: "Bundle Zapatilla + Medias", tipo: "Bundle", ambito: "Retail + eCommerce", ventaAtribuida: 1800000, ventaBaseline: 1200000, costo: 990000, costoPromo: 240000 },
  { id: 7, nombre: "2da unidad 50% Off", tipo: "MxN", ambito: "Todos los canales", ventaAtribuida: 2400000, ventaBaseline: 1800000, costo: 1440000, costoPromo: 600000 },
  { id: 8, nombre: "Puntos x3 Navidad", tipo: "Incentivo", ambito: "Retail", ventaAtribuida: 1600000, ventaBaseline: 1300000, costo: 880000, costoPromo: 160000 },
  { id: 9, nombre: "15% Off Calzado Formal", tipo: "% descuento", ambito: "Retail + eCommerce", ventaAtribuida: 900000, ventaBaseline: 850000, costo: 378000, costoPromo: 135000 },
  { id: 10, nombre: "Gift Card Día Madre", tipo: "Activación", ambito: "Todos los canales", ventaAtribuida: 1200000, ventaBaseline: 800000, costo: 600000, costoPromo: 200000 },
];

const getResultado = (roi: number, margen: number): { label: string; color: string } => {
  if (margen < 20) return { label: "Destruye margen", color: "red" };
  if (roi > 2) return { label: "Muy positivo", color: "green" };
  if (roi >= 1) return { label: "Bueno", color: "blue" };
  return { label: "Negativo", color: "red" };
};

const typeAnalysis = [
  { key: 1, tipo: "% descuento", promos: 3, ventaPromedio: 1700000, liftPromedio: 18.2, margenPromedio: 42.1, roiPromedio: 1.3 },
  { key: 2, tipo: "MxN", promos: 2, ventaPromedio: 3600000, liftPromedio: 42.5, margenPromedio: 38.8, roiPromedio: 1.5 },
  { key: 3, tipo: "Incentivo", promos: 2, ventaPromedio: 2400000, liftPromedio: 28.4, margenPromedio: 44.2, roiPromedio: 2.1 },
  { key: 4, tipo: "Bundle", promos: 1, ventaPromedio: 1800000, liftPromedio: 50.0, margenPromedio: 45.0, roiPromedio: 3.4 },
  { key: 5, tipo: "Beneficio", promos: 1, ventaPromedio: 2200000, liftPromedio: 37.5, margenPromedio: 45.0, roiPromedio: 3.3 },
  { key: 6, tipo: "Activación", promos: 1, ventaPromedio: 1200000, liftPromedio: 50.0, margenPromedio: 50.0, roiPromedio: 3.0 },
];

const repeatRecommend = [
  { key: 1, nombre: "Bundle Zapatilla + Medias", roi: 3.4, margen: 45.0, accion: "Repetir" },
  { key: 2, nombre: "Envío gratis > $50k", roi: 3.3, margen: 45.0, accion: "Repetir" },
  { key: 3, nombre: "Gift Card Día Madre", roi: 3.0, margen: 50.0, accion: "Repetir" },
  { key: 4, nombre: "Puntos x5 Black Friday", roi: 2.5, margen: 45.0, accion: "Repetir" },
  { key: 5, nombre: "3x2 Zapatillas Running", roi: 3.3, margen: 45.0, accion: "Repetir" },
  { key: 6, nombre: "2da unidad 50% Off", roi: 1.0, margen: 40.0, accion: "Ajustar" },
  { key: 7, nombre: "Flash Sale Outlet", roi: 0.9, margen: 35.0, accion: "Evitar" },
  { key: 8, nombre: "15% Off Calzado Formal", roi: 0.4, margen: 58.0, accion: "Ajustar" },
];

const bestCategories = [
  { key: 1, categoria: "Zapatillas Running", promos: 4, liftPromedio: 38.2, margenPromedio: 42.5 },
  { key: 2, categoria: "Indumentaria", promos: 3, liftPromedio: 28.4, margenPromedio: 48.1 },
  { key: 3, categoria: "Calzado Infantil", promos: 2, liftPromedio: 22.8, margenPromedio: 40.2 },
  { key: 4, categoria: "Zapatillas Urbanas", promos: 2, liftPromedio: 18.5, margenPromedio: 46.8 },
  { key: 5, categoria: "Calzado Formal", promos: 1, liftPromedio: 5.9, margenPromedio: 58.0 },
];

const bestProducts = [
  { key: 1, producto: "Zapatilla Aero Max 2026", lift: 52.4, margen: 44.2, ventaIncr: 1200000 },
  { key: 2, producto: "Remera DryFit Pro", lift: 38.6, margen: 50.8, ventaIncr: 680000 },
  { key: 3, producto: "Zapatilla Asfalto Pro", lift: 35.2, margen: 43.5, ventaIncr: 520000 },
  { key: 4, producto: "Sneaker Urban Classic", lift: 28.4, margen: 47.2, ventaIncr: 380000 },
  { key: 5, producto: "Campera Running Lite", lift: 22.1, margen: 41.8, ventaIncr: 290000 },
];

const insights = [
  "Pendiente: implementar motor de análisis automático de promociones.",
];

const PromotionAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const totalVentaAtribuida = allPromos.reduce((s, p) => s + p.ventaAtribuida, 0);
  const totalIncremental = allPromos.reduce((s, p) => s + (p.ventaAtribuida - p.ventaBaseline), 0);
  const totalCostoPromo = allPromos.reduce((s, p) => s + p.costoPromo, 0);
  const totalCosto = allPromos.reduce((s, p) => s + p.costo, 0);
  const avgLift = allPromos.reduce((s, p) => s + ((p.ventaAtribuida - p.ventaBaseline) / p.ventaBaseline) * p.ventaAtribuida, 0) / totalVentaAtribuida * 100;
  const avgMargen = ((totalVentaAtribuida - totalCosto) / totalVentaAtribuida) * 100;
  const avgROI = totalIncremental / totalCostoPromo;
  const posPromos = allPromos.filter((p) => (p.ventaAtribuida - p.ventaBaseline) / p.costoPromo > 1).length;
  const erosionPromos = allPromos.filter((p) => ((p.ventaAtribuida - p.costo) / p.ventaAtribuida) * 100 < 35).length;

  const filtered = useMemo(() => {
    let data = [...allPromos];
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter((p) => p.nombre.toLowerCase().includes(q));
    }
    return data;
  }, [search]);

  const columns = [
    { title: "Promoción", key: "nombre", render: (_: any, r: Promo) => <Text strong style={{ cursor: "pointer", color: "#1890ff" }}>{r.nombre}</Text> },
    { title: "Tipo", dataIndex: "tipo", key: "tipo", render: (v: string) => <Tag>{v}</Tag> },
    { title: "Ámbito", dataIndex: "ambito", key: "ambito", width: 140 },
    { title: "Venta atribuida", key: "ventaAtribuida", render: (_: any, r: Promo) => fmt(r.ventaAtribuida), align: "right" as const },
    { title: "Venta incremental", key: "ventaIncr", render: (_: any, r: Promo) => fmt(r.ventaAtribuida - r.ventaBaseline), align: "right" as const },
    { title: "Lift", key: "lift", render: (_: any, r: Promo) => r.ventaBaseline > 0 ? <Vr value={((r.ventaAtribuida - r.ventaBaseline) / r.ventaBaseline) * 100} /> : "—", align: "right" as const },
    { title: "Margen", key: "margen", render: (_: any, r: Promo) => `${(((r.ventaAtribuida - r.costo) / r.ventaAtribuida) * 100).toFixed(1)}%`, align: "right" as const },
    { title: "ROI", key: "roi", render: (_: any, r: Promo) => { const roi = (r.ventaAtribuida - r.ventaBaseline) / r.costoPromo; return <Text strong>{roi.toFixed(1)}x</Text>; }, align: "right" as const },
    { title: "Resultado", key: "resultado", render: (_: any, r: Promo) => { const roi = (r.ventaAtribuida - r.ventaBaseline) / r.costoPromo; const m = ((r.ventaAtribuida - r.costo) / r.ventaAtribuida) * 100; const res = getResultado(roi, m); return <Tag color={res.color}>{res.label}</Tag>; } },
  ];

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>Promociones</Title>

      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <Input placeholder="Buscar promoción..." prefix={<SearchOutlined />} value={search} onChange={(e) => setSearch(e.target.value)} className={styles.searchInput} allowClear />
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Período</Text><RangePicker className={styles.filterInput} /></div>
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Tipo</Text><Select placeholder="Todos" allowClear className={styles.filterSelect} options={[{ value: "desc", label: "% descuento" }, { value: "mxn", label: "MxN" }, { value: "inc", label: "Incentivo" }, { value: "bundle", label: "Bundle" }]} /></div>
          <div className={styles.filterItem}><Text className={styles.filterLabel}>Canal</Text><Select placeholder="Todos" allowClear className={styles.filterSelect} options={[{ value: "retail", label: "Retail" }, { value: "ecommerce", label: "eCommerce" }]} /></div>
        </div>
      </Card>

      {/* KPIs */}
      <div className={styles.kpiRow}>
        {[
          { label: "Promos ejecutadas", value: String(allPromos.length) },
          { label: "Venta atribuida", value: fmt(totalVentaAtribuida) },
          { label: "% venta en promo", value: `${((totalVentaAtribuida / VENTA_TOTAL) * 100).toFixed(1)}%` },
          { label: "Lift promedio", value: `${avgLift.toFixed(1)}%` },
          { label: "Margen medio", value: `${avgMargen.toFixed(1)}%` },
          { label: "ROI promedio", value: `${avgROI.toFixed(1)}x` },
          { label: "Resultado positivo", value: String(posPromos) },
          { label: "Erosión margen", value: String(erosionPromos) },
        ].map((k, i) => (
          <Card className={styles.kpiCard} key={i}>
            <Text className={styles.kpiLabel}>{k.label}</Text>
            <div className={styles.kpiValue}>{k.value}</div>
          </Card>
        ))}
      </div>

      {/* TABLE */}
      <Card className={styles.tableCard}>
        <Table columns={columns} dataSource={filtered.map((p) => ({ ...p, key: p.id }))} pagination={{ pageSize: 15, showSizeChanger: false }} size="small" className={styles.dataTable}
          onRow={(record) => ({ onClick: () => navigate(`/commercial-analysis/promotions/${record.id}`), style: { cursor: "pointer" } })} />
      </Card>

      {/* INSIGHTS (placeholder) */}
      <Card className={styles.sectionCard} title={<Space><BulbOutlined style={{ color: "#faad14" }} /><span>Insights</span></Space>}>
        <div className={styles.bulletList}>{insights.map((ins, i) => (<div key={i} className={styles.bulletItem}><BulbOutlined className={styles.bYellow} /><Text type="secondary">{ins}</Text></div>))}</div>
      </Card>

      {/* TYPE ANALYSIS + RECOMMENDATIONS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><BarChartOutlined /><span>Qué tipo funciona mejor</span></Space>}>
            <Table columns={[
              { title: "Tipo", dataIndex: "tipo", key: "tipo" },
              { title: "Promos", dataIndex: "promos", key: "promos", align: "center" as const },
              { title: "Venta prom.", dataIndex: "ventaPromedio", key: "ventaPromedio", render: (v: number) => fmt(v), align: "right" as const },
              { title: "Lift prom.", dataIndex: "liftPromedio", key: "liftPromedio", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
              { title: "Margen prom.", dataIndex: "margenPromedio", key: "margenPromedio", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
              { title: "ROI prom.", dataIndex: "roiPromedio", key: "roiPromedio", render: (v: number) => `${v.toFixed(1)}x`, align: "right" as const },
            ]} dataSource={typeAnalysis} pagination={false} size="small" className={styles.dataTable} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><TrophyOutlined /><span>Promos que vale la pena repetir</span></Space>}>
            <Table columns={[
              { title: "Promoción", dataIndex: "nombre", key: "nombre" },
              { title: "ROI", dataIndex: "roi", key: "roi", render: (v: number) => `${v.toFixed(1)}x`, align: "right" as const },
              { title: "Margen", dataIndex: "margen", key: "margen", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
              { title: "Acción", dataIndex: "accion", key: "accion", render: (v: string) => <Tag color={v === "Repetir" ? "green" : v === "Ajustar" ? "orange" : "red"}>{v}</Tag> },
            ]} dataSource={repeatRecommend} pagination={false} size="small" className={styles.dataTable} />
          </Card>
        </Col>
      </Row>

      {/* BEST CATEGORIES + PRODUCTS */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><PieChartOutlined /><span>Categorías que mejor responden</span></Space>}>
            <Table columns={[
              { title: "Categoría", dataIndex: "categoria", key: "categoria" },
              { title: "Promos", dataIndex: "promos", key: "promos", align: "center" as const },
              { title: "Lift prom.", dataIndex: "liftPromedio", key: "liftPromedio", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
              { title: "Margen prom.", dataIndex: "margenPromedio", key: "margenPromedio", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
            ]} dataSource={bestCategories} pagination={false} size="small" className={styles.dataTable} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className={styles.sectionCard} title={<Space><TrophyOutlined style={{ color: "#52c41a" }} /><span>Productos que mejor responden</span></Space>}>
            <Table columns={[
              { title: "Producto", dataIndex: "producto", key: "producto" },
              { title: "Lift", dataIndex: "lift", key: "lift", render: (v: number) => <Vr value={v} />, align: "right" as const },
              { title: "Margen", dataIndex: "margen", key: "margen", render: (v: number) => `${v.toFixed(1)}%`, align: "right" as const },
              { title: "Venta incr.", dataIndex: "ventaIncr", key: "ventaIncr", render: (v: number) => fmt(v), align: "right" as const },
            ]} dataSource={bestProducts} pagination={false} size="small" className={styles.dataTable} />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default PromotionAnalysis;
